/**
 * Maxlive88 Promo Panel — Link Analytics
 *
 * Click Tracker / Affiliate Link Analytics dashboard with:
 *  • KPI cards (clicks, signups, FTDs, conv rate, CPA earned, EPC)
 *  • Time-series chart (daily clicks vs signups vs deposits)
 *  • Top countries (GEO)
 *  • Sub-ID performance
 *  • Device breakdown
 *  • Recent clicks feed
 *  • Tracking link builder (sub_id / utm tags)
 *  • Postback API config + test
 *  • Push notification (PWA) enable/disable
 */
import { useEffect, useMemo, useState } from "react";
import {
  MousePointerClick, UserPlus, BadgeDollarSign, Activity, TrendingUp, Coins,
  Copy, BellRing, BellOff, Send, Globe2, Smartphone, Monitor, Tablet, Bot,
  Link2, Settings2, Webhook, ShieldOff, RefreshCcw, Filter,
} from "lucide-react";
import { toast } from "sonner";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
  PieChart, Pie, Cell,
} from "recharts";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { pushManager } from "../lib/push";

const fmtN = (v) => Number(v || 0).toLocaleString("en-IN");
const fmtMoney = (v) => `৳ ${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const fmtPct = (v) => `${Number(v || 0).toFixed(2)}%`;

const DEVICE_COLORS = { desktop: "#22c073", mobile: "#3b82f6", tablet: "#f59e0b", bot: "#9ca3af", unknown: "#64748b" };
const DEVICE_ICONS = { desktop: Monitor, mobile: Smartphone, tablet: Tablet, bot: Bot, unknown: Activity };

function Card({ children, className = "", style = {}, ...rest }) {
  return (
    <div
      className={`rounded-xl ${className}`}
      style={{
        background: "linear-gradient(180deg, rgba(20,18,30,0.94), rgba(10,8,18,0.96))",
        border: "1px solid rgba(34, 192, 115, 0.18)",
        ...style,
      }}
      {...rest}
    >
      {children}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub, color = "#22c073", testid }) {
  return (
    <Card className="p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-[10.5px] uppercase tracking-widest text-white/55 font-bold">
        <Icon size={12} style={{ color }} /> {label}
      </div>
      <div className="text-white font-black text-[22px] tabular-nums mt-1" style={{ color }}>
        {value}
      </div>
      {sub && <div className="text-[11px] text-white/45 mt-1">{sub}</div>}
    </Card>
  );
}

export default function Analytics() {
  const [days, setDays] = useState(30);
  const [subFilter, setSubFilter] = useState("");
  const [overview, setOverview] = useState(null);
  const [timeseries, setTimeseries] = useState([]);
  const [geo, setGeo] = useState([]);
  const [subIds, setSubIds] = useState([]);
  const [devices, setDevices] = useState([]);
  const [recent, setRecent] = useState([]);
  const [builder, setBuilder] = useState(null);
  const [postback, setPostback] = useState(null);
  const [pushState, setPushState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pbBusy, setPbBusy] = useState(false);

  // ── Builder form state ──
  const [bSubId, setBSubId] = useState("");
  const [bUtmSource, setBUtmSource] = useState("");
  const [bUtmMedium, setBUtmMedium] = useState("");
  const [bUtmCampaign, setBUtmCampaign] = useState("");

  // ── Postback form state ──
  const [pbUrl, setPbUrl] = useState("");
  const [pbSecret, setPbSecret] = useState("");
  const [pbEnabled, setPbEnabled] = useState(false);

  const loadAll = async () => {
    setLoading(true);
    const tasks = [
      ["overview", () => api.get(`/aff-panel/link-analytics/overview?days=${days}${subFilter ? `&sub_id=${encodeURIComponent(subFilter)}` : ""}`).then((r) => setOverview(r.data))],
      ["timeseries", () => api.get(`/aff-panel/link-analytics/timeseries?days=${days}`).then((r) => setTimeseries(r.data.items || []))],
      ["geo", () => api.get(`/aff-panel/link-analytics/geo?days=${days}`).then((r) => setGeo(r.data.items || []))],
      ["sub-ids", () => api.get(`/aff-panel/link-analytics/sub-ids?days=${days}`).then((r) => setSubIds(r.data.items || []))],
      ["devices", () => api.get(`/aff-panel/link-analytics/devices?days=${days}`).then((r) => setDevices(r.data.items || []))],
      ["recent", () => api.get(`/aff-panel/link-analytics/recent-clicks?limit=20`).then((r) => setRecent(r.data.items || []))],
      ["builder", () => api.get(`/aff-panel/link-analytics/builder`).then((r) => setBuilder(r.data))],
      ["postback", () => api.get(`/aff-panel/postback`).then((r) => { setPostback(r.data); setPbUrl(r.data.url || ""); setPbEnabled(!!r.data.enabled); })],
      ["push", () => pushManager.status("/aff-panel/push").then((s) => setPushState(s))],
    ];
    const results = await Promise.allSettled(tasks.map(([_n, fn]) => fn()));
    results.forEach((r, i) => {
      if (r.status === "rejected") {
        // eslint-disable-next-line no-console
        console.warn(`[Analytics] ${tasks[i][0]} failed:`, r.reason?.message || r.reason);
      }
    });
    setLoading(false);
  };

  useEffect(() => { loadAll(); }, [days, subFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const builtLink = useMemo(() => {
    if (!builder?.promo_code) return "";
    const base = builder.track_click_url;
    const params = new URLSearchParams({ code: builder.promo_code });
    if (bSubId) params.set("sub_id", bSubId);
    if (bUtmSource) params.set("utm_source", bUtmSource);
    if (bUtmMedium) params.set("utm_medium", bUtmMedium);
    if (bUtmCampaign) params.set("utm_campaign", bUtmCampaign);
    return `${base}?${params.toString()}`;
  }, [builder, bSubId, bUtmSource, bUtmMedium, bUtmCampaign]);

  const builtRedirect = useMemo(() => {
    if (!builder?.register_url) return "";
    const u = new URL(builder.register_url);
    if (bSubId) u.searchParams.set("sub_id", bSubId);
    if (bUtmSource) u.searchParams.set("utm_source", bUtmSource);
    if (bUtmMedium) u.searchParams.set("utm_medium", bUtmMedium);
    if (bUtmCampaign) u.searchParams.set("utm_campaign", bUtmCampaign);
    return u.toString();
  }, [builder, bSubId, bUtmSource, bUtmMedium, bUtmCampaign]);

  const copy = (text) => {
    if (!text) return;
    navigator.clipboard.writeText(text).then(
      () => toast.success("Copied"),
      () => toast.error("Copy failed")
    );
  };

  const savePostback = async () => {
    setPbBusy(true);
    try {
      const body = { url: pbUrl.trim(), enabled: pbEnabled };
      if (pbSecret.trim()) body.secret = pbSecret.trim();
      await api.patch("/aff-panel/postback", body);
      toast.success("Postback saved");
      setPbSecret("");
      const { data } = await api.get("/aff-panel/postback");
      setPostback(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setPbBusy(false);
    }
  };

  const testPostback = async () => {
    setPbBusy(true);
    try {
      const { data } = await api.post("/aff-panel/postback/test", {});
      if (data?.ok) toast.success(`Postback ok (status ${data.status_code})`);
      else toast.error(`Postback failed${data?.status_code ? ` (status ${data.status_code})` : ""}: ${data?.error || ""}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test failed");
    } finally {
      setPbBusy(false);
    }
  };

  const togglePush = async () => {
    try {
      if (pushState?.subscribed_local) {
        await pushManager.unsubscribe("/aff-panel/push");
        toast.success("Push notifications disabled");
      } else {
        await pushManager.subscribe("/aff-panel/push");
        toast.success("Push notifications enabled");
      }
      setPushState(await pushManager.status("/aff-panel/push"));
    } catch (e) {
      toast.error(e?.message || "Push toggle failed");
    }
  };

  const testPush = async () => {
    try {
      const r = await pushManager.sendTest("/aff-panel/push");
      if ((r?.sent || 0) > 0) toast.success(`Sent push to ${r.sent} device${r.sent === 1 ? "" : "s"}`);
      else toast.error("No active subscriptions");
    } catch (e) {
      toast.error(e?.message || "Test failed");
    }
  };

  return (
    <div data-testid="link-analytics-page" className="space-y-4">
      <PageHeader
        eyebrow="ANALYTICS"
        title="Link Analytics"
        subtitle="Click Tracker — Sub-ID, GEO, Device, CPA, Postback. Real-time click intelligence."
        right={
          <div className="flex items-center gap-2">
            <select
              value={days}
              onChange={(e) => setDays(parseInt(e.target.value, 10))}
              className="p-input-shell rounded-xl px-3 h-10 text-[13px]"
              data-testid="la-window-select"
            >
              <option value={7}>Last 7 days</option>
              <option value={14}>Last 14 days</option>
              <option value={30}>Last 30 days</option>
              <option value={60}>Last 60 days</option>
              <option value={90}>Last 90 days</option>
            </select>
            <button
              onClick={loadAll}
              className="h-10 px-3 rounded-xl text-[13px] font-bold inline-flex items-center gap-1.5 p-btn-ghost"
              data-testid="la-refresh-btn"
            >
              <RefreshCcw size={13} /> Refresh
            </button>
          </div>
        }
      />

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <StatCard icon={MousePointerClick} label="Clicks" value={fmtN(overview?.clicks)} sub={`${fmtN(overview?.unique_clicks)} unique`} color="#3b82f6" testid="kpi-clicks" />
        <StatCard icon={UserPlus} label="Signups" value={fmtN(overview?.signups)} sub={fmtPct(overview?.signup_rate)} color="#22c073" testid="kpi-signups" />
        <StatCard icon={BadgeDollarSign} label="FTDs" value={fmtN(overview?.ftds)} sub={fmtPct(overview?.ftd_rate)} color="#ffc933" testid="kpi-ftds" />
        <StatCard icon={TrendingUp} label="Conv. Rate" value={fmtPct(overview?.signup_rate)} sub="signup / unique" color="#a78bfa" testid="kpi-conv" />
        <StatCard icon={Coins} label="CPA Earned" value={fmtMoney(overview?.cpa_earned)} sub={overview?.cpa_enabled ? `@ ${fmtMoney(overview?.cpa_amount)} per FTD` : "CPA disabled"} color="#f59e0b" testid="kpi-cpa" />
        <StatCard icon={Activity} label="EPC" value={fmtMoney(overview?.epc)} sub={`Revenue ${fmtMoney(overview?.gross_revenue)}`} color="#10b981" testid="kpi-epc" />
      </div>

      {/* Time-series chart */}
      <Card className="p-4" data-testid="la-timeseries-card">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-white/55 font-bold mb-3">
          <Activity size={12} className="text-emerald-300" /> Daily timeseries — last {days} days
        </div>
        <div style={{ width: "100%", height: 280 }}>
          <ResponsiveContainer>
            <LineChart data={timeseries}>
              <CartesianGrid stroke="rgba(255,255,255,0.06)" strokeDasharray="3 3" />
              <XAxis dataKey="date" tick={{ fill: "#94a3b8", fontSize: 11 }} tickFormatter={(d) => (d || "").slice(5)} />
              <YAxis tick={{ fill: "#94a3b8", fontSize: 11 }} />
              <Tooltip contentStyle={{ background: "#0a0e1a", border: "1px solid rgba(255,255,255,0.12)", color: "#fff", fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line type="monotone" dataKey="clicks" name="Clicks" stroke="#3b82f6" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="unique_clicks" name="Unique" stroke="#22c073" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="signups" name="Signups" stroke="#ffc933" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>

      {/* GEO + Devices */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-4 md:col-span-2" data-testid="la-geo-card">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-white/55 font-bold mb-3">
            <Globe2 size={12} className="text-emerald-300" /> Top countries
          </div>
          {geo.length === 0 ? (
            <div className="py-10 text-center text-white/45 text-sm">No click data yet for this window.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[12.5px]">
                <thead className="text-white/45 text-[10px] uppercase tracking-widest">
                  <tr>
                    <th className="text-left py-2 pl-1">Country</th>
                    <th className="text-right py-2">Clicks</th>
                    <th className="text-right py-2">Unique</th>
                    <th className="text-right py-2 pr-1">Share</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {geo.map((g, i) => {
                    const total = geo.reduce((s, r) => s + r.clicks, 0) || 1;
                    const share = (g.clicks / total) * 100;
                    return (
                      <tr key={`${g.country}-${i}`} className="text-white" data-testid={`geo-row-${i}`}>
                        <td className="py-2 pl-1">
                          <span className="inline-flex items-center gap-2 font-bold">
                            <span className="px-2 py-0.5 rounded text-[10px] tracking-widest" style={{ background: "rgba(59,130,246,0.15)", color: "#60a5fa", border: "1px solid rgba(59,130,246,0.3)" }}>
                              {g.country || "XX"}
                            </span>
                          </span>
                        </td>
                        <td className="py-2 text-right tabular-nums">{fmtN(g.clicks)}</td>
                        <td className="py-2 text-right tabular-nums text-emerald-300">{fmtN(g.unique_clicks)}</td>
                        <td className="py-2 pr-1 text-right">
                          <div className="inline-flex items-center gap-2 w-28">
                            <div className="flex-1 h-1.5 rounded-full bg-white/10 overflow-hidden">
                              <div className="h-full" style={{ width: `${share}%`, background: "linear-gradient(90deg,#22c073,#3b82f6)" }} />
                            </div>
                            <span className="text-[10.5px] text-white/55 tabular-nums w-10 text-right">{share.toFixed(1)}%</span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <Card className="p-4" data-testid="la-devices-card">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-white/55 font-bold mb-3">
            <Smartphone size={12} className="text-emerald-300" /> Devices
          </div>
          {devices.length === 0 ? (
            <div className="py-10 text-center text-white/45 text-sm">No data</div>
          ) : (
            <>
              <div style={{ width: "100%", height: 180 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie
                      data={devices}
                      dataKey="clicks"
                      nameKey="device"
                      innerRadius={45}
                      outerRadius={75}
                      paddingAngle={2}
                    >
                      {devices.map((d, i) => (
                        <Cell key={i} fill={DEVICE_COLORS[d.device] || "#64748b"} stroke="#0a0d18" />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ background: "#0a0e1a", border: "1px solid rgba(255,255,255,0.12)", fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <ul className="space-y-1.5 mt-2">
                {devices.map((d) => {
                  const Icon = DEVICE_ICONS[d.device] || Activity;
                  return (
                    <li key={d.device} className="flex items-center justify-between text-[12px]">
                      <span className="inline-flex items-center gap-2 text-white/75">
                        <Icon size={11} style={{ color: DEVICE_COLORS[d.device] }} /> {d.device}
                      </span>
                      <span className="tabular-nums font-bold text-white">{fmtN(d.clicks)}</span>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </Card>
      </div>

      {/* Sub-ID performance */}
      <Card className="p-4" data-testid="la-subid-card">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-white/55 font-bold">
            <Filter size={12} className="text-emerald-300" /> Sub-ID performance
          </div>
          {subFilter && (
            <button
              onClick={() => setSubFilter("")}
              className="text-[11px] font-bold uppercase tracking-widest text-amber-300 hover:text-amber-200"
            >
              Clear filter
            </button>
          )}
        </div>
        {subIds.length === 0 ? (
          <div className="py-8 text-center text-white/45 text-sm">No clicks tagged with sub-IDs yet. Use the link builder below to add a sub_id parameter.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[12.5px]">
              <thead className="text-white/45 text-[10px] uppercase tracking-widest">
                <tr>
                  <th className="text-left py-2 pl-1">Sub-ID</th>
                  <th className="text-right py-2">Clicks</th>
                  <th className="text-right py-2">Unique</th>
                  <th className="text-right py-2 pr-1">Filter</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {subIds.map((s, i) => (
                  <tr key={`${s.sub_id}-${i}`} className="text-white" data-testid={`subid-row-${i}`}>
                    <td className="py-2 pl-1 font-mono text-emerald-200">{s.sub_id}</td>
                    <td className="py-2 text-right tabular-nums">{fmtN(s.clicks)}</td>
                    <td className="py-2 text-right tabular-nums text-emerald-300">{fmtN(s.unique_clicks)}</td>
                    <td className="py-2 pr-1 text-right">
                      {s.sub_id !== "(none)" && (
                        <button
                          onClick={() => setSubFilter(s.sub_id)}
                          className="text-[11px] font-bold uppercase tracking-widest text-emerald-300 hover:text-emerald-200"
                        >
                          Apply
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Tracking link builder */}
      <Card className="p-4" data-testid="la-builder-card">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-white/55 font-bold mb-3">
          <Link2 size={12} className="text-emerald-300" /> Tracking link builder
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {[
            ["sub_id", bSubId, setBSubId, "fb_campaign_1"],
            ["utm_source", bUtmSource, setBUtmSource, "facebook"],
            ["utm_medium", bUtmMedium, setBUtmMedium, "social"],
            ["utm_campaign", bUtmCampaign, setBUtmCampaign, "spring2026"],
          ].map(([key, val, set, ph]) => (
            <label key={key} className="block">
              <div className="text-[10px] uppercase tracking-widest text-white/45 font-semibold mb-1">{key}</div>
              <input
                value={val}
                onChange={(e) => set(e.target.value)}
                placeholder={ph}
                className="w-full p-input-shell rounded-lg px-3 h-10 text-[13px]"
                data-testid={`builder-${key}`}
              />
            </label>
          ))}
        </div>

        <div className="mt-3 space-y-2">
          {[
            ["Tracking URL (server-side click + cookie + redirect to register)", builtLink, "builder-tracking-url"],
            ["Register URL (direct)", builtRedirect, "builder-register-url"],
          ].map(([label, url, testid]) => (
            <div key={label}>
              <div className="text-[10.5px] uppercase tracking-widest text-white/45 font-semibold mb-1">{label}</div>
              <div className="flex items-center gap-2">
                <input
                  readOnly
                  value={url}
                  className="flex-1 p-input-shell rounded-lg px-3 h-10 text-[12px] text-emerald-200 font-mono"
                  data-testid={testid}
                />
                <button
                  onClick={() => copy(url)}
                  className="h-10 px-3 rounded-lg p-btn-ghost text-[12px] font-bold inline-flex items-center gap-1"
                  data-testid={`${testid}-copy`}
                >
                  <Copy size={12} /> Copy
                </button>
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Postback + PWA Push */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Postback */}
        <Card className="p-4" data-testid="la-postback-card">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-white/55 font-bold">
              <Webhook size={12} className="text-emerald-300" /> Postback API
            </div>
            <label className="flex items-center gap-2 text-[12px] text-white/75 select-none">
              <input
                type="checkbox"
                checked={pbEnabled}
                onChange={(e) => setPbEnabled(e.target.checked)}
                data-testid="postback-enabled"
              />
              Enabled
            </label>
          </div>
          <div className="space-y-2">
            <input
              value={pbUrl}
              onChange={(e) => setPbUrl(e.target.value)}
              placeholder="https://your-tracker.com/postback"
              className="w-full p-input-shell rounded-lg px-3 h-10 text-[13px]"
              data-testid="postback-url"
            />
            <input
              type="password"
              value={pbSecret}
              onChange={(e) => setPbSecret(e.target.value)}
              placeholder={postback?.secret_set ? "•••••••• (leave blank to keep)" : "Secret (optional, for HMAC signature)"}
              className="w-full p-input-shell rounded-lg px-3 h-10 text-[13px]"
              data-testid="postback-secret"
            />
            <div className="text-[10.5px] text-white/45 leading-relaxed">
              Events: click, signup, FTD, deposit, commission. Body is JSON. Signature header:{" "}
              <span className="font-mono text-emerald-200">x-maxlive88-signature</span> = HMAC-SHA256(secret, body).
            </div>
            <div className="flex items-center gap-2 pt-1">
              <button
                onClick={savePostback}
                disabled={pbBusy}
                className="h-10 px-4 rounded-lg p-btn-primary text-[13px] font-bold inline-flex items-center gap-1.5"
                data-testid="postback-save"
              >
                <Settings2 size={12} /> Save
              </button>
              <button
                onClick={testPostback}
                disabled={pbBusy || !postback?.url}
                className="h-10 px-4 rounded-lg p-btn-ghost text-[13px] font-bold inline-flex items-center gap-1.5"
                data-testid="postback-test"
              >
                <Send size={12} /> Send test
              </button>
            </div>
          </div>
        </Card>

        {/* PWA Push */}
        <Card className="p-4" data-testid="la-push-card">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-white/55 font-bold mb-3">
            <BellRing size={12} className="text-emerald-300" /> Push Notifications (PWA)
          </div>

          {!pushState?.supported ? (
            <div className="flex items-start gap-2 text-[12px] text-amber-300">
              <ShieldOff size={14} className="mt-0.5" />
              <span>Your browser doesn’t support push notifications. Try Chrome, Edge, or Firefox on desktop / Android.</span>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="rounded-lg bg-white/5 p-3">
                  <div className="text-[10px] uppercase tracking-widest text-white/45">Permission</div>
                  <div className="text-[13px] font-black mt-1" style={{ color: pushState.permission === "granted" ? "#22c073" : pushState.permission === "denied" ? "#e54b59" : "#ffc933" }}>
                    {pushState.permission}
                  </div>
                </div>
                <div className="rounded-lg bg-white/5 p-3">
                  <div className="text-[10px] uppercase tracking-widest text-white/45">Devices</div>
                  <div className="text-white font-black text-[15px] mt-1">{pushState.subscriptions || 0}</div>
                </div>
                <div className="rounded-lg bg-white/5 p-3">
                  <div className="text-[10px] uppercase tracking-widest text-white/45">This device</div>
                  <div className="text-[13px] font-black mt-1" style={{ color: pushState.subscribed_local ? "#22c073" : "#94a3b8" }}>
                    {pushState.subscribed_local ? "Subscribed" : "Off"}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 mt-3">
                <button
                  onClick={togglePush}
                  className="h-10 px-4 rounded-lg p-btn-primary text-[13px] font-bold inline-flex items-center gap-1.5"
                  data-testid="push-toggle"
                >
                  {pushState.subscribed_local ? <><BellOff size={12} /> Disable</> : <><BellRing size={12} /> Enable on this device</>}
                </button>
                {pushState.subscribed_local && (
                  <button
                    onClick={testPush}
                    className="h-10 px-4 rounded-lg p-btn-ghost text-[13px] font-bold inline-flex items-center gap-1.5"
                    data-testid="push-test"
                  >
                    <Send size={12} /> Send test push
                  </button>
                )}
              </div>
              <div className="text-[10.5px] text-white/45 leading-relaxed mt-3">
                You will get a real-time push for: new signups under your code, player deposits, FTDs, and commission credits — even when this tab is closed.
              </div>
            </>
          )}
        </Card>
      </div>

      {/* Recent clicks live feed */}
      <Card className="p-4" data-testid="la-recent-card">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-white/55 font-bold mb-3">
          <MousePointerClick size={12} className="text-emerald-300" /> Recent clicks
        </div>
        {recent.length === 0 ? (
          <div className="py-8 text-center text-white/45 text-sm">No clicks tracked yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[12px]">
              <thead className="text-white/45 text-[10px] uppercase tracking-widest">
                <tr>
                  <th className="text-left py-2 pl-1">When</th>
                  <th className="text-left py-2">Sub-ID</th>
                  <th className="text-left py-2">UTM</th>
                  <th className="text-left py-2">Country</th>
                  <th className="text-left py-2">Device</th>
                  <th className="text-left py-2 pr-1">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {recent.map((r, i) => (
                  <tr key={r.id || i} className="text-white">
                    <td className="py-2 pl-1 text-white/65 text-[11px]">{(r.created_at || "").slice(0, 19).replace("T", " ")}</td>
                    <td className="py-2 text-emerald-200 font-mono text-[11px]">{r.sub_id || "—"}</td>
                    <td className="py-2 text-white/65 text-[11px]">
                      {[r.utm_source, r.utm_medium, r.utm_campaign].filter(Boolean).join(" · ") || "—"}
                    </td>
                    <td className="py-2">
                      <span className="px-1.5 py-0.5 rounded text-[10px] tracking-widest" style={{ background: "rgba(59,130,246,0.15)", color: "#60a5fa", border: "1px solid rgba(59,130,246,0.3)" }}>
                        {r.country || "XX"}
                      </span>
                    </td>
                    <td className="py-2 text-white/75">{r.device || "?"}</td>
                    <td className="py-2 pr-1">
                      {r.blocked_by_geo ? (
                        <span className="text-[10.5px] font-bold text-red-300">BLOCKED</span>
                      ) : r.is_bot ? (
                        <span className="text-[10.5px] font-bold text-amber-300">BOT</span>
                      ) : r.is_unique ? (
                        <span className="text-[10.5px] font-bold text-emerald-300">UNIQUE</span>
                      ) : (
                        <span className="text-[10.5px] font-bold text-white/55">REPEAT</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {loading && <div className="text-center text-white/45 text-sm py-3">Loading…</div>}
    </div>
  );
}
